import { pollWhileVisible, type components } from "@roastery/api-client";
import { useApi } from "@roastery/auth";
import { useQueryClient } from "@tanstack/react-query";

export type Lead = components["schemas"]["Lead"];
// springdoc leaves record components optional in the schema, hence the NonNullable wrapper.
export type LeadKind = NonNullable<Lead["kind"]>;

export const LEADS_PATH = "/api/v1/platform/leads";
export const LEADS_COUNT_PATH = "/api/v1/platform/leads/count";

export function useLeads(handled: boolean | undefined, kind: LeadKind | "") {
  const api = useApi();
  return api.query.useQuery("get", LEADS_PATH, {
    params: { query: { handled, kind: kind || undefined, page: 0, size: 50 } },
  });
}

/**
 * The badge beside "Leads" in the sidebar. Polled rather than pushed: a lead arrives at most a few
 * times a day, and a minute of staleness on a number nobody is waiting for is not worth a socket.
 */
export function useOpenLeadCount() {
  const api = useApi();
  return api.query.useQuery("get", LEADS_COUNT_PATH, {}, pollWhileVisible(60_000));
}

function useInvalidateLeads() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ["get", LEADS_PATH] });
    void queryClient.invalidateQueries({ queryKey: ["get", LEADS_COUNT_PATH] });
  };
}

export function useMarkLeadHandled() {
  const api = useApi();
  const invalidate = useInvalidateLeads();
  return api.query.useMutation("post", "/api/v1/platform/leads/{id}/handled", { onSuccess: invalidate });
}

export function useRenotifyLead() {
  const api = useApi();
  const invalidate = useInvalidateLeads();
  return api.query.useMutation("post", "/api/v1/platform/leads/{id}/renotify", { onSuccess: invalidate });
}

/**
 * A `mailto:` that opens the operator's own mail client with the subject already written. The whole
 * of "replying" in US-14.10 — building an inbox of our own would be building a worse one.
 */
export function mailtoFor(lead: Lead, subject: string): string {
  return `mailto:${lead.email ?? ""}?subject=${encodeURIComponent(subject)}`;
}
